import type { WireSourceKind } from '@logistics/contracts';

/**
 * The eight source types of the design's "Add source" catalog, in its order.
 * A type is selectable only when the API publishes a descriptor for it; the
 * three with no plugin at all are "coming soon". The texts say what the
 * adapter actually reads (its README), never what a vendor might offer.
 */
interface CatalogSeed {
  readonly id: string;
  /** The descriptor this type is backed by; absent for "coming soon". */
  readonly kind?: WireSourceKind['kind'];
  readonly glyph: string;
  readonly name: string;
  readonly auth: string;
  readonly short: string;
  readonly namePlaceholder: string;
}

const SEEDS: readonly CatalogSeed[] = [
  {
    id: 'quickmanage',
    kind: 'quickmanage',
    glyph: 'QM',
    name: 'QuickManage TMS',
    auth: 'CLIENT ID + SECRET',
    short: 'Trips, drivers, trucks and trailers.',
    namePlaceholder: 'QuickManage · Main account',
  },
  {
    id: 'email',
    kind: 'email',
    glyph: 'EM',
    name: 'Email (IMAP / Gmail)',
    auth: 'APP PASSWORD',
    short: 'Broker mail in one IMAP folder.',
    namePlaceholder: 'dispatch@company.com',
  },
  {
    id: 'telegram',
    kind: 'telegram',
    glyph: 'TG',
    name: 'Telegram account',
    auth: 'MTPROTO SESSION',
    short: 'Driver chats and dispatcher groups.',
    namePlaceholder: 'Dispatch phone',
  },
  {
    id: 'samsara',
    kind: 'samsara',
    glyph: 'SA',
    name: 'Samsara',
    auth: 'API TOKEN',
    short: 'Vehicles, HOS clocks and trips.',
    namePlaceholder: 'Samsara · Fleet',
  },
  {
    id: 'gomotive',
    kind: 'motive',
    glyph: 'GM',
    name: 'GoMotive',
    auth: 'API KEY',
    short: 'Vehicles, drivers, live locations and HOS clocks.',
    namePlaceholder: 'GoMotive · Fleet',
  },
  {
    id: 'whatsapp',
    glyph: 'WA',
    name: 'WhatsApp',
    auth: 'COMING SOON',
    short: 'Driver and shipper WhatsApp threads.',
    namePlaceholder: 'WhatsApp · Dispatch line',
  },
  {
    id: 'gdrive',
    glyph: 'GD',
    name: 'Google Drive',
    auth: 'COMING SOON',
    short: 'Rate sheets, contracts, SOP docs.',
    namePlaceholder: 'Drive · Ops folder',
  },
  {
    id: 'webhook',
    glyph: 'API',
    name: 'Custom API / webhook',
    auth: 'COMING SOON',
    short: 'Any HTTP source your team maintains.',
    namePlaceholder: 'Internal load-tracking API',
  },
];

export type CatalogState = 'available' | 'unavailable' | 'coming-soon';

export interface CatalogEntry extends CatalogSeed {
  readonly state: CatalogState;
  readonly descriptor?: WireSourceKind;
  /** Why it cannot be picked, when it cannot. */
  readonly reason?: string;
}

/** The catalog as this deployment can offer it. */
export function catalogEntries(descriptors: readonly WireSourceKind[]): CatalogEntry[] {
  return SEEDS.map((seed) => {
    const descriptor =
      seed.kind === undefined ? undefined : descriptors.find((d) => d.kind === seed.kind);
    if (descriptor === undefined) {
      return { ...seed, state: 'coming-soon', reason: 'No plugin for this source yet.' };
    }
    if (descriptor.unavailable !== undefined) {
      return { ...seed, state: 'unavailable', descriptor, reason: descriptor.unavailable };
    }
    return { ...seed, state: 'available', descriptor };
  });
}

/** The catalog entry a configured kind belongs to (motive → GoMotive). */
export function entryForKind(kind: string): CatalogSeed | undefined {
  return SEEDS.find((seed) => seed.kind === kind);
}

const READS: Readonly<Record<string, string>> = {
  quickmanage:
    'Trips (loads) from your QuickManage account, and live lookups of trucks, trailers, drivers, customers, dispatchers and finance reports when a question needs them. It never writes back.',
  email:
    'New mail in the chosen IMAP folder of this mailbox. Only mail from the chosen history window is read on the first connect.',
  telegram:
    'Messages in the chats of a Telegram user account (not a bot), read over MTProto. The session string is the account, so it is stored encrypted and never shown again.',
  samsara:
    'Vehicles, hours-of-service clocks and trips from your Samsara organisation, plus live lookups when a question needs current data.',
  motive:
    'Vehicles and drivers from your Motive company, plus live lookups of vehicle and driver locations and hours-of-service clocks when a question needs current data. It never writes back.',
};

/** "WHAT THE AGENT READS" for a configured kind. */
export function whatItReads(kind: string): string {
  return READS[kind] ?? 'Records from this source, as its plugin maps them.';
}

/** "SETUP GUIDE": the descriptor's own help texts, one step per field that has one. */
export function guideSteps(descriptor: WireSourceKind | undefined): string[] {
  if (descriptor === undefined) return [];
  return descriptor.fields
    .filter((f) => f.help !== undefined && f.help !== '')
    .map((f) => `${f.label}: ${f.help ?? ''}`);
}

/** "01", "02" — the design's step numbers. */
export function stepNumber(index: number): string {
  return String(index + 1).padStart(2, '0');
}
